import type {ReactElement} from 'react';
import React, {useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {ShadowMobileColorPanel} from '@Panels/poster-editor-item-shadow-panel/components/shadow-mobile-color-panel';
import {SlideshowShadowMobileColorPanel} from '@Panels/poster-editor-item-shadow-panel/components/slideshow-shadow-mobile-color-panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {TranscriptShadowMobileColorPanel} from '@Panels/poster-editor-item-shadow-panel/components/transcript-shadow-mobile-color-panel/transcript-shadow-mobile-color-panel';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {ItemMobileShadowOptions} from '@Panels/poster-editor-item-shadow-panel/components/item-mobile-shadow-options/item-mobile-shadow-options';

interface PosterEditorItemShadowPanelProps {
  panelId: string;
}

enum ShadowPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PANEL = 'COLOR_PANEL',
}

export default function PosterEditorItemShadowPanel({...props}: PosterEditorItemShadowPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [panelToShow, setPanelToShow] = useState(ShadowPanels.DEFAULT);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && !activeSlide) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_shadow')} />;
  };

  const getContextualMenuContent = (): ReactElement => {
    return (
      <ItemMobileShadowOptions
        panelId={props.panelId}
        itemType={activeItem.gitype}
        onClickColorOption={() => {
          setPanelToShow(ShadowPanels.COLOR_PANEL);
        }}
      />
    );
  };

  const getPanelContent = (): ReactElement => {
    return (
      <ContextualMenu id="shadow-mobile-panel" hasMobileModalPadding hasMobileMorphingProperties>
        {getContextualMenuContent()}
      </ContextualMenu>
    );
  };

  const getColorPanel = (): ReactElement | null => {
    if (activeItem.isSlideshow()) {
      return (
        <SlideshowShadowMobileColorPanel
          onBack={(): void => {
            setPanelToShow(ShadowPanels.DEFAULT);
          }}
          panelId={props.panelId}
          onClose={onClose}
        />
      );
    }

    if (activeItem.isTranscript()) {
      return (
        <TranscriptShadowMobileColorPanel
          onBack={(): void => {
            setPanelToShow(ShadowPanels.DEFAULT);
          }}
          panelId={props.panelId}
          onClose={onClose}
        />
      );
    }

    return (
      <ShadowMobileColorPanel
        onBack={(): void => {
          setPanelToShow(ShadowPanels.DEFAULT);
        }}
        panelId={props.panelId}
        onClose={onClose}
      />
    );
  };

  const getPanels = (): ReactElement | null => {
    switch (panelToShow) {
      case ShadowPanels.COLOR_PANEL:
        return getColorPanel();
      case ShadowPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getPanelContent()} />;
    }
  };

  return getPanels();
}
